/**
 * File Export Modal Component
 * Allows users to export content to various file formats
 */
import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { exportToFile, generateFilename } from '../utils/fileExport';

interface FileExportModalProps {
  isOpen: boolean;
  content: string;
  defaultTitle?: string;
  onClose: () => void;
}

type ExportFormat = 'docx' | 'xlsx' | 'md' | 'txt';

export default function FileExportModal({ isOpen, content, defaultTitle, onClose }: FileExportModalProps) {
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>('docx');
  const [filename, setFilename] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update filename when defaultTitle or format changes
  useEffect(() => {
    if (defaultTitle) {
      const extension = `.${selectedFormat}`;
      setFilename(generateFilename(defaultTitle, extension));
    }
  }, [defaultTitle, selectedFormat]);

  // Auto-focus and select filename input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 50);
    }
  }, [isOpen]);

  const handleExport = async () => {
    if (!filename.trim()) {
      toast.error('Please enter a filename');
      return;
    }

    if (!content.trim()) {
      toast.error('No content to export');
      return;
    }

    setIsExporting(true);

    try {
      await exportToFile({
        format: selectedFormat,
        content,
        filename: filename.trim(),
      });

      toast.success(`File exported successfully as ${selectedFormat.toUpperCase()}`);
      onClose();
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Failed to export file: ${(error as Error).message}`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'Enter' && !isExporting) {
      handleExport();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-dark-card border border-dark-border rounded-lg shadow-xl max-w-lg w-full p-6">
        <h2 className="text-xl font-bold mb-4 text-dark-text">Export to File</h2>

        <div className="space-y-4">
          {/* File Format Selection */}
          <div>
            <label className="block text-sm font-medium text-dark-text-muted mb-2">
              File Format
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setSelectedFormat('docx')}
                disabled={isExporting}
                className={`px-4 py-3 rounded-md border-2 transition-all ${
                  selectedFormat === 'docx'
                    ? 'border-accent-orange bg-accent-orange/10 text-accent-orange'
                    : 'border-dark-border text-dark-text-muted hover:border-dark-text-muted'
                }`}
              >
                <div className="flex flex-col items-center gap-1">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                  </svg>
                  <span className="text-sm font-medium">Word (DOCX)</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setSelectedFormat('xlsx')}
                disabled={isExporting}
                className={`px-4 py-3 rounded-md border-2 transition-all ${
                  selectedFormat === 'xlsx'
                    ? 'border-accent-orange bg-accent-orange/10 text-accent-orange'
                    : 'border-dark-border text-dark-text-muted hover:border-dark-text-muted'
                }`}
              >
                <div className="flex flex-col items-center gap-1">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0112 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5" />
                  </svg>
                  <span className="text-sm font-medium">Excel (XLSX)</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setSelectedFormat('md')}
                disabled={isExporting}
                className={`px-4 py-3 rounded-md border-2 transition-all ${
                  selectedFormat === 'md'
                    ? 'border-accent-orange bg-accent-orange/10 text-accent-orange'
                    : 'border-dark-border text-dark-text-muted hover:border-dark-text-muted'
                }`}
              >
                <div className="flex flex-col items-center gap-1">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
                  </svg>
                  <span className="text-sm font-medium">Markdown (MD)</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setSelectedFormat('txt')}
                disabled={isExporting}
                className={`px-4 py-3 rounded-md border-2 transition-all ${
                  selectedFormat === 'txt'
                    ? 'border-accent-orange bg-accent-orange/10 text-accent-orange'
                    : 'border-dark-border text-dark-text-muted hover:border-dark-text-muted'
                }`}
              >
                <div className="flex flex-col items-center gap-1">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                  </svg>
                  <span className="text-sm font-medium">Text (TXT)</span>
                </div>
              </button>
            </div>
          </div>

          {/* Filename Input */}
          <div>
            <label htmlFor="export-filename" className="block text-sm font-medium text-dark-text-muted mb-2">
              Filename
            </label>
            <input
              ref={inputRef}
              id="export-filename"
              type="text"
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isExporting}
              className="w-full px-3 py-2 bg-dark-bg border border-dark-border rounded-md text-dark-text focus:outline-none focus:ring-2 focus:ring-accent-orange focus:border-transparent disabled:opacity-50"
              placeholder="Enter filename..."
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isExporting}
              className="px-4 py-2 bg-dark-bg hover:bg-dark-border text-dark-text rounded-md transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleExport}
              disabled={isExporting || !filename.trim()}
              className="px-4 py-2 bg-accent-orange hover:bg-accent-orange/80 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isExporting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Exporting...</span>
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  <span>Export File</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
